import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Container, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, TablePagination, TextField } from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setStatus,
  setLimit,
  setSkip,
  setCurrentPage,
  reset as resetBundleCategory,
} from '../../store/bundleCategory/bundleCategorySlice';
import {
  findBundleCategory,
  createBundleCategory,
  patchBundleCategory,
} from '../../store/bundleCategory/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Scrollbar from '../../components/Scrollbar';
import BundleCategoryFormModal from './BundleCategoryFormModal';
import { BundleCategory } from '../../store/bundleCategory/api/interface';
import bundleCategorySchema from '../../validation/bundleCategorySchema';
import { omit } from 'lodash';
import {
  reset as resetSelectBundleCategory,
  setStatus as setSelectBundleCategoryStatus,
} from '../../store/selectBundleCategory';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, status, isLoading, totalRecords, limit, currentPage, skip } =
    useAppSelector((state) => state.bundleCategory);
  const [search, setSearch] = useState<string>('');
  const [activeStateFilter, setActiveStateFilter] = useState<string>('');
  const { toggle, onOpen, onClose } = useToggle();

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findBundleCategory({
          limit,
          skip,
          ...(search && { search }),
          ...(activeStateFilter !== '' && {
            activeState: activeStateFilter === 'true',
          }),
        })
      );
    }
  }, [dispatch, status]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  useEffect(() => {
    dispatch(resetBundleCategory());
  }, [search, activeStateFilter]);

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      emoji: '',
      position: '',
      activeState: true,
      showOnHomePage: false,
    },
    validationSchema: bundleCategorySchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchBundleCategory(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createBundleCategory(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectBundleCategory());
        dispatch(setSelectBundleCategoryStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: BundleCategory): void => {
    onOpen();
    setValues({
      id: value.id,
      labelEn: value?.labelEn,
      labelAr: value?.labelAr,
      emoji: value?.emoji || '',
      position: String(value?.position ?? ''),
      activeState: value?.activeState,
      showOnHomePage:
        value?.showOnHomePage === true ||
        String(value?.showOnHomePage) === 'true',
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const handleOpenCreate = (): void => {
    resetForm();
    onOpen();
  };

  return (
    <>
      <Page title={'Bundle Category: List'}>
        <Container maxWidth={'xl'}>
          <HeaderBreadcrumbs
            heading={'Bundle Category'}
            links={[
              { name: 'Dashboard', href: '/' },
              { name: 'Bundle Category' },
            ]}
            action={
              <Button
                variant='contained'
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={handleOpenCreate}
              >
                New Category
              </Button>
            }
          />

          <Card>
            <Grid
              container
              spacing={2}
              alignItems='center'
              sx={{ py: 2.5, px: 3 }}
            >
              <Grid
                item
                xs={12}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label='Status'
                    value={activeStateFilter}
                    onChange={(e) => setActiveStateFilter(e.target.value)}
                  >
                    <MenuItem value=''>All Status</MenuItem>
                    <MenuItem value='true'>Active</MenuItem>
                    <MenuItem value='false'>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
              >
                <TextField
                  fullWidth
                  onChange={handleSearch}
                  placeholder='Search...'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Iconify
                          icon={'eva:search-fill'}
                          sx={{ color: 'text.disabled', width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative' }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Emoji'}</TableCell>
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Home Page'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell align='center'>{item?.labelEn}</TableCell>
                          <TableCell align='center'>{item?.labelAr}</TableCell>
                          <TableCell align='center'>
                            {item?.emoji || ' '}
                          </TableCell>
                          <TableCell align='center'>{item?.position}</TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={item.activeState ? 'success' : 'error'}
                            >
                              {item.activeState ? 'Active' : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={
                                item.showOnHomePage === true ||
                                String(item.showOnHomePage) === 'true'
                                  ? 'success'
                                  : 'default'
                              }
                            >
                              {item.showOnHomePage === true ||
                              String(item.showOnHomePage) === 'true'
                                ? 'Yes'
                                : 'No'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(item)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <BundleCategoryFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>
      </Page>
    </>
  );
};

export default Index;
